import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, Circle } from "react-leaflet";
import { api } from "../lib/api";
import { TID } from "../constants/testIds";
import { parcelIcon } from "../lib/mapIcons";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  CheckCircle2, Clock, Shield, MapPin, IndianRupee, Ruler, Building,
  Scale, X, SlidersHorizontal, List, Train, Plane, School, Briefcase,
  ShoppingBag, Hospital, Beer, Route,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useCompare } from "../lib/compare";
import InquiryDialog from "../components/InquiryDialog";
import SignalsTicker from "../components/SignalsTicker";
import JsonLd, { buildParcelJsonLd } from "../components/JsonLd";
import { parcelWhatsappUrl } from "../lib/whatsapp";
import { MessageCircle } from "lucide-react";

const CORRIDORS_STATIC = ["Devanahalli", "Sarjapur", "Dommasandra", "Hoskote", "Bidadi", "Whitefield"];
const CONVERSIONS = ["All", "Agricultural", "Converted NA", "Industrial", "Residential Master Plan"];
const OWNERS = ["All", "Single-Owner", "General Category", "Joint Family", "Corporate"];

const HEAT_ZONES = [
  { name: "Devanahalli", lat: 13.2437, lng: 77.7101, radius: 6000, intensity: 0.85 },
  { name: "Sarjapur", lat: 12.8452, lng: 77.7844, radius: 5500, intensity: 0.9 },
  { name: "Dommasandra", lat: 12.8781, lng: 77.7444, radius: 4500, intensity: 0.7 },
  { name: "Hoskote", lat: 13.0692, lng: 77.7961, radius: 5000, intensity: 0.65 },
  { name: "Bidadi", lat: 12.8000, lng: 77.3833, radius: 4800, intensity: 0.55 },
  { name: "Whitefield", lat: 12.9698, lng: 77.7500, radius: 4200, intensity: 0.75 },
];

const CATEGORY_ICON = {
  "Metro Station": Train,
  "Airport & Highway": Plane,
  "Schools & Colleges": School,
  "Tech Parks & Commercial": Briefcase,
  "Lifestyle & Healthcare": ShoppingBag,
};
const HOSPITAL_ICONS = { hospital: Hospital, beer: Beer, road: Route, mall: ShoppingBag };

const fmtINR = (v) => {
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
  return `₹${v}`;
};

export default function MapDashboard() {
  const navigate = useNavigate();
  const compare = useCompare();
  const [parcels, setParcels] = useState([]);
  const [infra, setInfra] = useState([]);
  const [selected, setSelected] = useState(null);
  const [nearby, setNearby] = useState(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [filters, setFilters] = useState({ corridor: "All", conversion_status: "All", owner_category: "All", min_score: 0, max_price: 8_00_00_000 });
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [mobileList, setMobileList] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, i] = await Promise.all([api.get("/parcels"), api.get("/infra")]);
        setParcels(p.data); setInfra(i.data);
      } catch { toast.error("Failed to load map data"); }
      finally { setLoading(false); }
    })();
  }, []);

  // Deep-link support: /map?p=<parcel_id> opens the detail dialog for that parcel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("p");
    if (!pid || parcels.length === 0) return;
    const found = parcels.find((x) => x.id === pid);
    if (found) openParcel(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcels]);

  const filtered = useMemo(() => {
    return parcels.filter((p) => {
      if (filters.corridor !== "All" && p.corridor_location !== filters.corridor) return false;
      if (filters.conversion_status !== "All" && p.conversion_status !== filters.conversion_status) return false;
      if (filters.owner_category !== "All" && p.owner_category !== filters.owner_category) return false;
      if (p.legal_score < filters.min_score) return false;
      if (p.price_per_acre > filters.max_price) return false;
      return true;
    });
  }, [parcels, filters]);

  // Build the corridor list dynamically from db + a stable known set so newly-added corridors auto-appear.
  const CORRIDORS = useMemo(() => {
    const set = new Set(CORRIDORS_STATIC);
    for (const p of parcels) if (p.corridor_location) set.add(p.corridor_location);
    return ["All", ...Array.from(set).sort()];
  }, [parcels]);

  const openParcel = async (p) => {
    setMobileList(false);
    setNearby(null);
    setNearbyLoading(true);
    // Update the URL so the tab can be shared / crawled with a canonical link
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("p", p.id);
      window.history.replaceState({}, "", url.toString());
    } catch {}
    try {
      const { data } = await api.get(`/parcels/${p.id}`);
      setSelected(data);
    } catch { toast.error("Failed to load parcel"); setNearbyLoading(false); return; }
    try {
      const { data } = await api.get(`/nearby?lat=${p.lat}&lng=${p.lng}&radius_km=15`);
      setNearby(data);
    } catch { /* nearby is best-effort */ }
    finally { setNearbyLoading(false); }
  };

  const closeParcel = () => {
    setSelected(null);
    setNearby(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("p");
      window.history.replaceState({}, "", url.toString());
    } catch {}
  };

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <FSelect label="Corridor" tid={TID.filterCorridor} value={filters.corridor} onChange={(v) => setFilters({ ...filters, corridor: v })} options={CORRIDORS} />
        <FSelect label="Land Type" tid={TID.filterConversion} value={filters.conversion_status} onChange={(v) => setFilters({ ...filters, conversion_status: v })} options={CONVERSIONS} />
        <FSelect label="Owner" tid={TID.filterOwner} value={filters.owner_category} onChange={(v) => setFilters({ ...filters, owner_category: v })} options={OWNERS} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Min Score: <span className="text-[#10B981]">{filters.min_score}</span></div>
          <Slider data-testid={TID.filterMinScore} value={[filters.min_score]} min={0} max={10} step={1} onValueChange={(v) => setFilters({ ...filters, min_score: v[0] })} />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Max Price / Acre: <span className="text-[#10B981]">{fmtINR(filters.max_price)}</span></div>
        <Slider data-testid={TID.filterMaxPrice} value={[filters.max_price]} min={5000000} max={80000000} step={1000000} onValueChange={(v) => setFilters({ ...filters, max_price: v[0] })} />
      </div>
      <div className="text-xs text-slate-500">{loading ? "Loading…" : `${filtered.length} of ${parcels.length} parcels`}</div>
    </div>
  );

  const listPanel = (
    <div className="p-4 space-y-3">
      {filtered.map((p, i) => {
        const inCompare = compare.has(p.id);
        return (
          <div key={p.id} style={{ animationDelay: `${i * 20}ms` }} data-testid={TID.parcelCard}
            className={`fade-up group rounded-xl border ${inCompare ? "border-[#10B981] ring-1 ring-[#10B981]/40" : "border-[#334155]"} bg-[#1E293B] p-4 hover:border-[#10B981] cursor-pointer active:scale-[0.99] transition`}
            onClick={() => openParcel(p)}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-white leading-tight truncate">{p.title}</div>
                <div className="mt-1 text-xs text-slate-400 flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" />{p.corridor_location}
                  <span data-testid="land-type-badge" className={`ml-1 rounded-full px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] ${p.land_type === "Plot" ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-emerald-500/15 text-[#10B981] border-emerald-500/40"}`}>{p.land_type || "Land"}</span>
                </div>
              </div>
              {p.verified ? (
                <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/15 shrink-0"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-500/40 text-amber-400 shrink-0"><Clock className="h-3 w-3 mr-1" />Audit</Badge>
              )}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <Metric icon={Ruler} label="Size" value={`${p.size_acres} Ac`} />
              <Metric icon={Building} label="Khata" value={p.khata_type} />
              <Metric icon={Shield} label="Score" value={`${p.legal_score}/10`} />
            </div>
            {typeof p.neighbourhood_score === "number" && (
              <div className="mt-3 flex items-center gap-2">
                <NScorePill score={p.neighbourhood_score} />
                <div className="flex-1 h-1 rounded-full bg-[#0F172A] overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${p.neighbourhood_score}%`, background: nColor(p.neighbourhood_score) }} />
                </div>
              </div>
            )}
            <div className="mt-3 flex items-center justify-between">
              <div className="text-[#10B981] font-semibold flex items-center"><IndianRupee className="h-3.5 w-3.5" />{fmtINR(p.price_per_acre).replace("₹", "")}<span className="text-slate-500 text-[10px] ml-1">/acre</span></div>
              <button data-testid={TID.compareToggle}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!inCompare && compare.full) { toast.info("Comparison holds up to 3 parcels. Remove one first."); return; }
                  compare.toggle(p.id);
                  if (!inCompare) toast.success("Added to comparison");
                }}
                className={`text-[10px] uppercase tracking-[0.2em] rounded-full px-3 py-1.5 border transition-colors ${inCompare ? "bg-[#10B981] text-white border-[#10B981]" : "border-[#334155] text-slate-400 hover:text-[#10B981] hover:border-[#10B981]"}`}>
                {inCompare ? "✓ Comparing" : "+ Compare"}
              </button>
            </div>
          </div>
        );
      })}
      {!loading && filtered.length === 0 && <div className="text-center text-slate-500 py-10 text-sm">No parcels match your filters.</div>}
    </div>
  );

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col md:flex-row relative">
      <div data-testid={TID.mapContainer} className="flex-1 relative min-h-[420px]">
        <MapContainer center={[12.97, 77.65]} zoom={10} className="h-full w-full" scrollWheelZoom
          zoomControl={typeof window !== "undefined" && window.innerWidth >= 768} tap>
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            subdomains={["a", "b", "c", "d"]}
          />
          {HEAT_ZONES.map((z) => (
            <Circle key={z.name} center={[z.lat, z.lng]} radius={z.radius}
              pathOptions={{ color: "#10B981", weight: 1, fillColor: "#10B981", fillOpacity: 0.08 + z.intensity * 0.08 }} />
          ))}
          {infra.map((i) =>
            i.type === "Ring Road" && i.name.includes("STRR") ? (
              <Polygon key={i.id} positions={i.coords} pathOptions={{ color: i.color, weight: 4, fillOpacity: 0.06, dashArray: "6 4" }}>
                <Popup><b>{i.name}</b><br />{i.status} · ETA {i.completion_year}</Popup>
              </Polygon>
            ) : i.type === "Ring Road" || i.type === "TechPark" ? (
              <Polygon key={i.id} positions={i.coords} pathOptions={{ color: i.color, weight: 2, fillOpacity: 0.05, dashArray: "4 4" }}>
                <Popup><b>{i.name}</b><br />{i.status} · ETA {i.completion_year}</Popup>
              </Polygon>
            ) : (
              <Polyline key={i.id} positions={i.coords} pathOptions={{ color: i.color, weight: 4, opacity: 0.85, dashArray: i.name.includes("Widening") ? "8 6" : undefined }}>
                <Popup><b>{i.name}</b><br />{i.type} · {i.status}<br />ETA {i.completion_year}</Popup>
              </Polyline>
            )
          )}
          {filtered.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={parcelIcon(p.verified)}
              eventHandlers={{ click: () => openParcel(p) }}>
              <Popup>
                <div className="min-w-[220px]">
                  <div className="font-semibold text-white">{p.title}</div>
                  <div className="text-xs text-slate-300 mt-1">{p.corridor_location} · {p.size_acres} Ac · {p.khata_type}</div>
                  <div className="text-[#10B981] mt-1 font-medium">{fmtINR(p.price_per_acre)} / acre</div>
                </div>
              </Popup>
            </Marker>
          ))}
          {selected && (
            <Circle center={[selected.lat, selected.lng]} radius={5000}
              pathOptions={{ color: "#10B981", weight: 2, fillColor: "#10B981", fillOpacity: 0.05, dashArray: "4 6" }} />
          )}
        </MapContainer>

        {/* Mobile floating search / filter bar */}
        <div className="md:hidden absolute top-3 left-3 right-3 z-[500] flex flex-col gap-2">
          <SignalsTicker />
          <div className="flex gap-2">
            <button data-testid="mobile-filters-open" onClick={() => setMobileFilters(true)}
              className="glass flex-1 h-11 rounded-full px-4 flex items-center gap-2 text-sm text-slate-200 justify-center">
              <SlidersHorizontal className="h-4 w-4 text-[#10B981]" /> Filter parcels
            </button>
            <button data-testid="mobile-list-open" onClick={() => setMobileList(true)}
              className="glass h-11 w-11 rounded-full grid place-items-center">
              <List className="h-4 w-4 text-[#10B981]" />
            </button>
          </div>
        </div>

        {/* Desktop signals ticker */}
        <div className="hidden md:block absolute top-4 left-4 right-4 z-[500] max-w-3xl">
          <SignalsTicker />
        </div>

        <div className="hidden md:block glass absolute bottom-4 left-4 rounded-lg p-4 text-xs space-y-2 max-w-xs z-[500]">
          <div className="uppercase tracking-[0.2em] text-slate-400 text-[10px]">Legend</div>
          <div className="flex items-center gap-2"><span className="h-1 w-6 bg-[#10B981]" /> STRR</div>
          <div className="flex items-center gap-2"><span className="h-1 w-6 bg-[#F59E0B]" /> IRR / PRR</div>
          <div className="flex items-center gap-2"><span className="h-1 w-6 bg-[#3B82F6]" /> Metro Blue</div>
          <div className="flex items-center gap-2"><span className="h-1 w-6 bg-[#EF4444]" /> Metro Red</div>
          <div className="flex items-center gap-2"><span className="h-1 w-6 bg-[#EC4899]" /> Metro Pink</div>
          <div className="flex items-center gap-2"><span className="h-1 w-6 bg-[#8B5CF6]" /> KWIN / Swift / TechParks</div>
          <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#10B981]" /> Verified parcel</div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-[420px] lg:w-[460px] flex-shrink-0 h-full border-l border-[#334155] bg-[#0F172A] flex-col">
        <div className="p-5 border-b border-[#334155] space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Growth Radar</div>
            <h2 className="mt-1 font-display text-xl font-semibold">Bangalore Parcels</h2>
          </div>
          {filterPanel}
        </div>
        <ScrollArea className="flex-1">{listPanel}</ScrollArea>
      </aside>

      {/* Mobile filter drawer */}
      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="bottom" className="bg-[#0F172A] border-[#334155] rounded-t-3xl max-h-[85vh] overflow-y-auto" data-testid="mobile-filters-sheet">
          <SheetHeader>
            <SheetTitle className="text-white font-display text-xl text-left">Filter parcels</SheetTitle>
          </SheetHeader>
          <div className="mt-6">{filterPanel}</div>
          <Button className="mt-6 w-full h-11 rounded-full bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => setMobileFilters(false)}>Show {filtered.length} parcels</Button>
        </SheetContent>
      </Sheet>

      {/* Mobile list drawer */}
      <Sheet open={mobileList} onOpenChange={setMobileList}>
        <SheetContent side="bottom" className="bg-[#0F172A] border-[#334155] rounded-t-3xl h-[85vh] overflow-y-auto p-0" data-testid="mobile-list-sheet">
          <SheetHeader className="p-5 border-b border-[#334155]">
            <SheetTitle className="text-white font-display text-xl text-left">{filtered.length} parcels</SheetTitle>
          </SheetHeader>
          {listPanel}
        </SheetContent>
      </Sheet>

      {/* Compare floating bar */}
      {compare.count > 0 && (
        <div className="fixed bottom-4 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-6 z-[1000] glass rounded-full px-4 py-2.5 md:px-5 md:py-3 flex items-center gap-3 md:gap-4 shadow-2xl">
          <Scale className="h-4 w-4 text-[#10B981]" />
          <span className="text-xs md:text-sm text-white flex-1 md:flex-initial"><b>{compare.count}</b>/3 to compare</span>
          <Button data-testid={TID.compareBarOpen} size="sm" onClick={() => navigate("/compare")} className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white h-8 text-xs">
            Compare now
          </Button>
          <button onClick={() => compare.clear()} className="text-slate-400 hover:text-white p-1" aria-label="clear"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Parcel detail dialog (public, no auth) */}
      <ParcelDetailDialog
        parcel={selected}
        nearby={nearby}
        nearbyLoading={nearbyLoading}
        onClose={closeParcel}
        onInquire={() => setInquiryOpen(true)}
      />
      <InquiryDialog open={inquiryOpen} onClose={() => setInquiryOpen(false)} parcel={selected} />

      {/* JSON-LD RealEstateListing structured data — only when a parcel is open */}
      {selected && (
        <JsonLd
          keyId="acrepulse-parcel-ld"
          data={buildParcelJsonLd(selected, { url: typeof window !== "undefined" ? window.location.href : undefined })}
        />
      )}    </div>
  );
}

function FSelect({ label, value, onChange, options, tid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={tid} className="bg-[#1E293B] border-[#334155] h-10 text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5 min-w-0">
      <Icon className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <div className="text-white text-xs font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

function nColor(s) {
  if (s >= 75) return "#10B981";
  if (s >= 50) return "#F59E0B";
  return "#EF4444";
}
function NScorePill({ score }) {
  const c = nColor(score);
  return (
    <div data-testid="parcel-nscore" className="text-[10px] uppercase tracking-[0.15em] rounded-full px-2 py-0.5 border shrink-0"
      style={{ color: c, borderColor: c + "55", background: c + "18" }}>
      Liveable <span className="font-bold tabular-nums ml-1">{score}</span>
    </div>
  );
}

function ParcelDetailDialog({ parcel, nearby, nearbyLoading, onClose, onInquire }) {
  if (!parcel) return null;
  return (
    <Dialog open={!!parcel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid={TID.parcelDetailOpen}>
        <div className="p-5 md:p-6 border-b border-[#334155] sticky top-0 bg-[#1E293B]/95 backdrop-blur-md z-10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl md:text-2xl tracking-tight leading-tight">{parcel.title}</DialogTitle>
            <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-400 mt-1 flex items-center gap-2">
              <span>{parcel.corridor_location} · {parcel.conversion_status}</span>
              <span data-testid="detail-land-type-badge" className={`rounded-full px-2 py-0.5 border text-[10px] ${parcel.land_type === "Plot" ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-emerald-500/15 text-[#10B981] border-emerald-500/40"}`}>{parcel.land_type || "Land"}</span>
              {parcel.land_type === "Plot" && typeof parcel.price_per_acre === "number" && parcel.size_sqft > 0 && (
                <span data-testid="detail-price-per-sqft" className="rounded-full px-2 py-0.5 border border-[#334155] text-slate-300">
                  ₹{Math.round(parcel.price_per_acre / parcel.size_sqft).toLocaleString()}/sqft
                </span>
              )}
            </div>
          </DialogHeader>
        </div>

        <div className="p-5 md:p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {[
              ["Size", `${parcel.size_acres} Ac`],
              ["Road", `${parcel.road_width_ft} ft`],
              ["Owner", parcel.owner_category],
              ["Khata", parcel.khata_type],
              ["Price/Acre", fmtINR(parcel.price_per_acre)],
              ["Est. FAR", parcel.far_multiplier],
              ["Max Height", `${parcel.max_height_m} m`],
              ["Setback", parcel.setback_ok ? "OK" : "Review"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-[#0F172A] border border-[#334155] p-3">
                <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">{k}</div>
                <div className="text-white font-medium mt-1 text-sm truncate">{v}</div>
              </div>
            ))}
          </div>

          {typeof parcel.neighbourhood_score === "number" && (
            <div data-testid="parcel-detail-nscore" className="rounded-lg border border-[#334155] bg-[#0F172A] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Neighbourhood Score</div>
                  <div className="mt-1 font-display text-3xl font-semibold tabular-nums" style={{ color: nColor(parcel.neighbourhood_score) }}>
                    {parcel.neighbourhood_score}<span className="text-sm text-slate-500 ml-1">/100</span>
                  </div>
                </div>
                <NScorePill score={parcel.neighbourhood_score} />
              </div>
              <div className="mt-3 h-2 rounded-full bg-[#1E293B] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${parcel.neighbourhood_score}%`, background: nColor(parcel.neighbourhood_score) }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">Weighted by proximity to metro, schools, tech parks, healthcare and highways within 15 km.</p>
            </div>
          )}

          {/* Nearby amenities */}
          <div>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Nearby (within 15 km)</h3>
              <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 text-[10px]">{nearby?.total ?? "…"} POIs</Badge>
            </div>
            <p className="mt-1 text-xs text-slate-500">Distances are straight-line (haversine)</p>
            {nearbyLoading && !nearby && <div className="mt-3 text-sm text-slate-500">Calculating distances…</div>}
            {nearby && Object.keys(nearby.results || {}).length === 0 && <div className="mt-3 text-sm text-slate-500">No mapped amenities within 15 km.</div>}
            {nearby && (
              <div className="mt-4 grid md:grid-cols-2 gap-3">
                {Object.entries(nearby.results).map(([cat, items]) => {
                  const Ic = CATEGORY_ICON[cat] || MapPin;
                  return (
                    <div key={cat} data-testid={`nearby-${cat.replace(/\s+/g, "-")}`} className="rounded-lg border border-[#334155] bg-[#0F172A] p-4">
                      <div className="flex items-center gap-2 text-[#10B981]">
                        <Ic className="h-4 w-4" />
                        <div className="text-xs uppercase tracking-[0.15em]">{cat}</div>
                      </div>
                      <ul className="mt-3 space-y-2">
                        {items.map((it) => {
                          const RowIc = HOSPITAL_ICONS[it.icon] || Ic;
                          return (
                            <li key={it.name} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <RowIc className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                                <span className="text-slate-300 truncate">{it.name}</span>
                              </div>
                              <span className="text-[#10B981] tabular-nums text-xs whitespace-nowrap ml-2">{it.distance_km} km</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Scorecard */}
          <div>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">10-Point Legal Scorecard</h3>
              <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{parcel.legal_score}/10</Badge>
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-2">
              {parcel.scorecard?.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2.5 rounded border border-[#334155] bg-[#0F172A]">
                  <div className="flex items-center gap-2 min-w-0">
                    {s.status === "pass" ? <CheckCircle2 className="h-4 w-4 text-[#10B981] shrink-0" /> : <Clock className="h-4 w-4 text-amber-400 shrink-0" />}
                    <span className="truncate">{s.item}</span>
                  </div>
                  <span className={s.status === "pass" ? "text-[#10B981] text-xs" : "text-amber-400 text-xs"}>{s.status === "pass" ? "OK" : "Pending"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-[#334155] bg-[#1E293B]/95 backdrop-blur-md p-4 flex flex-col-reverse sm:flex-row gap-3">
          <Button data-testid={TID.parcelDetailClose} onClick={onClose} variant="outline" className="border-[#334155] bg-transparent hover:bg-[#0F172A] hover:text-white text-white rounded-full flex-1 sm:flex-initial">Close</Button>
          <a
            data-testid="parcel-whatsapp-btn"
            href={parcelWhatsappUrl(parcel, { mode: "single" })}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white h-10 px-5 font-medium text-sm transition-colors flex-1"
          >
            <MessageCircle className="h-4 w-4" /> Enquire on WhatsApp
          </a>
          <Button onClick={onInquire} data-testid="parcel-inquiry-btn" className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white flex-1">
            Request Brochure
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
